import {Lazy} from '../utils/lazy';

export type DynamicEntry<T> = {
  head: Lazy<T>;
  tail: Dynamiclist<T>;
} | null;

export type Dynamiclist<T> = (T?) => DynamicEntry<T>

export function dynamicMap<T>(block: (T) => T, list: Dynamiclist<T>, updatedHead: T | null = null): Dynamiclist<T> {
  return () => {
    const source = (updatedHead == null) ? list() : list(updatedHead);
    if (source == null) {
      return null;
    } else {
      const modifiedHead = block(source.head());
      return {
        head: () => modifiedHead,
        tail: (head = modifiedHead) => dynamicMap(block, source.tail, head)()
      };
    }
  };
}


export function dynamicMapFromIndex<T>(block: (T) => T, targetIndex: number, list: Dynamiclist<T>, previousHead: T | null = null): Dynamiclist<T> {
  return () => {
    const source = (previousHead == null) ? list() : list(previousHead);
    if (source == null) {
      return null;
    } else {
      if (targetIndex <= 0) {
        const modifiedHead = block(source.head())
        return {
          head: () => modifiedHead,
          tail: (head = modifiedHead) => dynamicMapFromIndex(block, 0, source.tail, head)()
        };
      } else {
        const oldHead = source.head()
        return {
          head: () => oldHead,
          tail: (head: T = oldHead) => dynamicMapFromIndex(block, targetIndex - 1, source.tail, head)()
        };
      }
    }
  };
}


export function take<T>(count: number, list: Dynamiclist<T>, updatedHead: T | null = null): Dynamiclist<T> {
  return () => {
    const source = (updatedHead == null) ? list() : list(updatedHead);

    if (count == 0 || source == null) {
      return null;
    } else {
      const modifiedHead = source.head();
      return {
        head: () => modifiedHead,
        tail: (head: T = modifiedHead) => take(count - 1, source.tail, head)()
      };
    }
  };
}

export function dynamicMapAtIndex<T>(block: (T) => T, targetIndex: number, list: Dynamiclist<T>, currentIndex: number = 0, previousHead: T | null = null): Dynamiclist<T> {
  return () => {
    const source = (previousHead == null) ? list() : list(previousHead);
    if (source == null) {
      return null;
    } else {
      if (targetIndex === currentIndex) {
        const modifiedHead = block(source.head())
        return {
          head: () => modifiedHead,
          tail: (head = modifiedHead) => source.tail(head)
        };
      } else {
        const oldHead = source.head()
        return {
          head: () => oldHead,
          tail: (head: T = oldHead) => dynamicMapAtIndex(block, targetIndex, source.tail, currentIndex + 1, head)()
        };
      }
    }
  };
}

export function dynamicRange(start: number = 1): Dynamiclist<number> {
  return () => ({
    head: () => start,
    tail: (head: number = start) => dynamicRange(head + 1)()
  });
}
